import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Buildings, Image as ImageIcon, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import GstinLookupField from "@/components/GstinLookupField";
import StateSelector from "@/components/StateSelector";

const emptyForm = {
  name: "", gstin: "", address: "", phone: "", whatsapp_number: "", email: "",
  state: "", state_code: "", logo_url: "",
  upi_id: "", bank_account_name: "", bank_account_number: "", bank_ifsc: "", bank_name: "",
};

const fileToLogo = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, 480 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    const png = c.toDataURL("image/png");
    resolve(png.length < 450000 ? png : c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => reject(new Error("Could not read this image"));
  img.src = URL.createObjectURL(file);
});

const LogoPicker = ({ value, onChange }) => {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { toast.error("Upload a PNG, JPG or WEBP image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB"); return; }
    try { onChange(await fileToLogo(file)); } catch (err) { toast.error(err.message); }
  };
  return (
    <div className="flex items-center gap-3" data-testid="company-logo-picker">
      <div className="w-20 h-20 rounded border border-border bg-white flex items-center justify-center overflow-hidden">
        {value ? <img src={value} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="company-logo-preview" /> : <ImageIcon size={26} className="text-muted-foreground" />}
      </div>
      <label className="inline-flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-md border border-border cursor-pointer hover:bg-muted">
        <UploadSimple size={14} />{value ? "Change Logo" : "Upload Logo"}
        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pick} data-testid="company-logo-input" />
      </label>
      {value && <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={() => onChange("")} data-testid="company-logo-remove">Remove</Button>}
    </div>
  );
};

export default function Companies() {
  const { reloadCompanies } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/companies"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/companies/${editing.id}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company created");
      }
      setOpen(false); load(); reloadCompanies();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this company? All its data (customers, invoices) will remain but become inaccessible.")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Deleted"); load(); reloadCompanies(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Companies</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startAdd} className="h-9" data-testid="company-add-btn">
              <Plus size={15} weight="bold" className="mr-1.5" /> New Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading tracking-tight">{editing ? "Edit Company" : "New Company"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <GstinLookupField
                  value={form.gstin}
                  onChange={(v) => setForm({ ...form, gstin: v })}
                  onResolved={(r) => {
                    const patch = {};
                    if (r.state) patch.state = r.state;
                    if (r.state_code) patch.state_code = r.state_code;
                    const nameFromApi = r.apply_name || r.trade_name || r.legal_name;
                    if (nameFromApi && !form.name) patch.name = nameFromApi;
                    else if (r.apply_name) patch.name = r.apply_name;
                    const addrFromApi = r.address && (r.pincode ? `${r.address} — ${r.pincode}` : r.address);
                    if (addrFromApi && !form.address) patch.address = addrFromApi;
                    setForm((f) => ({ ...f, ...patch }));
                  }}
                  label="GSTIN"
                  testid="company-gstin"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="eyebrow">Company Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">GSTIN</Label>
                <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="font-mono" data-testid="company-gstin-legacy" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">WhatsApp Number</Label>
                <Input inputMode="tel" value={form.whatsapp_number}
                  onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                  placeholder="10-digit business WhatsApp"
                  data-testid="company-whatsapp" className="font-mono" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="eyebrow">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="eyebrow">Address</Label>
                <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="col-span-2">
                <StateSelector
                  state={form.state}
                  stateCode={form.state_code}
                  onChange={({ state, state_code }) => setForm((f) => ({ ...f, state, state_code }))}
                  testid="company-state"
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="eyebrow">Company Logo <span className="text-muted-foreground">— used on invoices, quotations, salary slips, offer letters & all company documents</span></Label>
                <LogoPicker value={form.logo_url} onChange={(logo_url) => setForm((f) => ({ ...f, logo_url }))} />
              </div>

              {/* Payment details — used to render the pay-QR on public invoices */}
              <div className="col-span-2 pt-3 mt-2 border-t border-border/70">
                <div className="eyebrow mb-2">Payment Details (for invoice / quotation QR)</div>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">UPI ID (VPA) <span className="text-muted-foreground">— shows a pay-QR on every invoice</span></Label>
                <Input value={form.upi_id}
                  onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                  placeholder="e.g. yourname@okicici" className="font-mono"
                  data-testid="company-upi-id" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Bank Name</Label>
                <Input value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="e.g. ICICI Bank"
                  data-testid="company-bank-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">A/C Holder Name</Label>
                <Input value={form.bank_account_name}
                  onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })}
                  data-testid="company-bank-holder" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">A/C Number</Label>
                <Input value={form.bank_account_number}
                  onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })}
                  className="font-mono"
                  data-testid="company-bank-account" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="eyebrow">IFSC Code</Label>
                <Input value={form.bank_ifsc}
                  onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })}
                  className="font-mono uppercase"
                  placeholder="e.g. ICIC0001234"
                  data-testid="company-bank-ifsc" />
              </div>

              <DialogFooter className="col-span-2 mt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="company-save">{editing ? "Update" : "Create"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-3 text-muted-foreground text-sm">Loading…</div>
        ) : rows.map((c) => (
          <div key={c.id} className="card-flat p-6" data-testid={`company-card-${c.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 bg-primary/10 flex items-center justify-center rounded-md">
                <Buildings size={20} className="text-primary" weight="regular" />
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(c)} data-testid={`company-edit-${c.id}`}><PencilSimple size={13} /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del(c.id)} data-testid={`company-delete-${c.id}`}><Trash size={13} /></Button>
              </div>
            </div>
            <div className="font-heading font-semibold text-lg tracking-tight">{c.name}</div>
            <div className="text-[11.5px] text-muted-foreground font-mono mt-1">{c.gstin || "GSTIN pending"}</div>
            <div className="mt-4 space-y-1 text-[12.5px]">
              <div className="text-muted-foreground">{c.address || "—"}</div>
              <div>{c.state} {c.state_code && `(${c.state_code})`}</div>
              <div className="text-muted-foreground">{c.phone} · {c.email}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
